import {
  CascaderField,
  DateRangePickerField,
  Form,
  NumericFormatField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { genRecursiveOptions, getRecursiveArray } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { groupBy } from 'lodash-es';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useExpenditurePurposes } from '../../hooks';
import { ExpenditurePurposeFormSchema as ExpenditurePurposeForMajorMatterFormSchema } from '../../models';
import { expenditurePurposeCodeLength } from '../../utils';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof ExpenditurePurposeForMajorMatterFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const MajorMatterForm: FC<Props> = ({ initialValues, onSubmit }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: { ...initialValues, level: 2 },
    resolver: zodResolver(ExpenditurePurposeForMajorMatterFormSchema),
  });
  const expenditurePurposes = useExpenditurePurposes({
    params: { isMajorMatter: true },
  });

  const groupedExpenditurePurposes = Object.entries(
    groupBy(
      getRecursiveArray(
        expenditurePurposes.data?.filter((d) => !d.upperId) ?? [],
        (expenditurePurposes.data ?? []).filter((d) => d.level <= 1),
      ),
      'accountingId',
    ),
  ).flatMap(([accountingId, data]) => {
    const accounting = data.find(
      (d) => d.accountingId === accountingId,
    )?.accounting;
    if (!accounting) {
      return [];
    }
    return {
      value: accountingId,
      label: accounting.name,
      children: genRecursiveOptions(data, 'id', 'name'),
    };
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        errors={errors}
        disableSeparator
        max={getCodeMaxValue(expenditurePurposeCodeLength(true))}
        required
      />

      <TextInputField
        name="name"
        autocompleteKey="expenditurePurpose.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      <CascaderField
        name="upperIds"
        label={t('upper-expenditure-level-2')}
        control={control}
        options={groupedExpenditurePurposes}
        errors={errors}
        required
      />

      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <NumericFormatField
        name="sortOrder"
        label={t('display-sort-order')}
        control={control}
        errors={errors}
        required
        allowNegative={false}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
